import { cn } from '@neo/lib/utils'
import { Command } from '@neo/primitives/command'
import { Label } from '@neo/primitives/label'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Typography } from '@neo/primitives/typography'
import { Cross2Icon } from '@radix-ui/react-icons'
import { cva } from 'class-variance-authority'
import { ChevronDown } from 'lucide-react'
import React, { useEffect } from 'react'
import { TabContent } from 'v2/pages/conversations/components/all-conversation-actions/share-view/team-user-select/select-menu'
import { useUsersQuery } from 'v2/pages/settings/queries'

export interface SelectOption {
  value: string
  label: string | React.ReactNode
  searchValue?: string
  [key: string]: unknown
}

export interface AgentSelectProps {
  name?: string
  label?: string
  value?: string[]
  onChange?: (values: string[]) => void
  placeholder?: string
  className?: string
  multiple?: boolean
  clearable?: boolean
  disabled?: boolean
  variant?: 'default' | 'ghost'
  popoverContentClassName?: string
  size?: 'sm' | 'md' | 'lg'
  helperText?: string
  error?: React.ReactNode
  excludeIds?: string[]
  initialValues?: string[]
}

interface MultiSelectValueProps {
  option: SelectOption
  handleSelect: (value: string) => void
}

const MultiSelectValue: React.FC<MultiSelectValueProps> = ({ option, handleSelect }) => (
  <span className="inline-flex items-center gap-1 rounded-md bg-bg3 py-0.5 pl-2 pr-1 text-xs font-medium text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 max-w-[150px]">
    <span className="truncate max-w-full" title={option.searchValue || (option.label as string)}>
      {option.label}
    </span>
    <button
      aria-label={`Remove ${option.label}`}
      type="button"
      onClick={(e) => {
        e.preventDefault()
        handleSelect(option.value)
      }}
      className="flex items-center rounded-sm px-[1px] text-muted-foreground/60 hover:text-muted-foreground focus-visible"
    >
      <Cross2Icon />
    </button>
  </span>
)

const selectStyles = cva('leading-none', {
  variants: {
    variant: {
      ghost: 'w-28',
      default: 'w-full',
    },
    size: {
      sm: 'min-h-[26px]',
      md: 'min-h-[36px]',
      lg: 'min-h-[42px]',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
})

export const AgentSelect = React.forwardRef<HTMLInputElement, AgentSelectProps>(
  (
    {
      placeholder,
      className,
      value = [],
      label,
      onChange,
      multiple = true,
      disabled,
      clearable = false,
      variant = 'default',
      size = 'md',
      name,
      popoverContentClassName = 'w-[var(--radix-popover-trigger-width)]',
      helperText = '',
      error,
      excludeIds = [],
      initialValues = [],
    },
    ref,
  ) => {
    const [userOptions, setUserOptions] = React.useState<{ label: string; value: string }[]>([])
    const [isOpen, setIsOpen] = React.useState(false)
    const { data: users } = useUsersQuery()

    useEffect(() => {
      if (users && Array.isArray(users?.members_list)) {
        const userSelectOptions = users?.members_list
          .filter(({ status }) => status === 'active')
          .filter(({ aom_uuid }) => !excludeIds.includes(aom_uuid))
          .map((userItem) => ({
            label: userItem.account_name,
            value: userItem.aom_uuid,
            type: 'aom',
          }))
        setUserOptions(userSelectOptions)
      }
    }, [users, excludeIds])

    const handleSelect = (selectedValue: string) => {
      const newValue = value.includes(selectedValue)
        ? value.filter((v) => v !== selectedValue)
        : [...value, selectedValue]
      onChange?.(newValue)
    }

    const handleClear = () => {
      onChange?.([])
    }

    const displayMultipleValues = value && Array.isArray(value)
    const selectedOptions = userOptions?.filter(
      (option) => Array.isArray(value) && value.includes(option.value),
    )
    // Only show badges for newly added users (not in initial values)
    const newlySelectedOptions = selectedOptions?.filter(
      (option) => !initialValues.includes(option.value),
    )

    let content: React.ReactNode
    if (displayMultipleValues && newlySelectedOptions && newlySelectedOptions.length > 0) {
      content = (
        <>
          {newlySelectedOptions.slice(0, 2).map((option) => (
            <MultiSelectValue key={option.value} option={option} handleSelect={handleSelect} />
          ))}
          {newlySelectedOptions.length > 2 && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md bg-bg3 py-0.5 pl-2 pr-1 text-xs font-medium text-foreground truncate max-w-[150px] transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              onClick={() => setIsOpen(true)}
            >
              +{newlySelectedOptions.length - 2}
            </button>
          )}
        </>
      )
    } else {
      content = <span className="mr-auto text-muted-foreground">{placeholder}</span>
    }

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen} modal>
        <PopoverTrigger asChild className={cn(disabled && 'pointer-events-none')}>
          <div className={cn(selectStyles({ variant, size }), className)}>
            {label && (
              <Label className="mb-1.5 block" htmlFor={name}>
                {label}
              </Label>
            )}
            <button
              type="button"
              id={name}
              className={cn(
                'flex w-full focus-visible cursor-pointer items-center px-3 py-1 data-[state=open]:border-ring',
                selectStyles({ size }),
                {
                  'justify-end border-none focus:ring-0 w-20': variant === 'ghost',
                  'justify-between border rounded-md bg-background': variant === 'default',
                },
                disabled ? 'bg-bg3 text-muted-foreground pointer-events-none' : '',
                isOpen && variant !== 'ghost' && 'ring-ring ring-2',
                error && 'border-status-danger',
                className,
              )}
            >
              <div
                className={cn(
                  'items-center gap-1 overflow-hidden text-sm',
                  multiple ? 'flex flex-grow flex-wrap' : 'truncate',
                )}
              >
                {content}
              </div>

              <div className="flex items-center pl-1 text-muted-foreground/60 hover:text-foreground">
                {clearable && (Array.isArray(value) ? value.length > 0 : value) ? (
                  <button
                    aria-label="Clear"
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      handleClear()
                    }}
                  >
                    <Cross2Icon className="size-4" />
                  </button>
                ) : (
                  <ChevronDown className="size-4" />
                )}
              </div>
            </button>

            {helperText && (
              <Typography variant="XS/400" className="text-tertiary mt-2">
                {helperText}
              </Typography>
            )}
            {error && (
              <Typography variant="XS/400" className="text-status-danger mt-2">
                {error}
              </Typography>
            )}
          </div>
        </PopoverTrigger>

        <PopoverContent className={cn('p-0', popoverContentClassName)} align="start">
          <Command shouldFilter={false}>
            <div className="w-full">
              <TabContent
                handleSelect={handleSelect}
                options={userOptions}
                value={value}
                ref={ref}
              />
            </div>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)

AgentSelect.displayName = 'AgentSelect'

export default AgentSelect
